import {initializeApp} from "firebase/app";
import {
    GoogleAuthProvider,
    signInWithPopup,
    getAuth,
    onAuthStateChanged,
    onIdTokenChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    updateProfile,
    sendEmailVerification,
    sendPasswordResetEmail,
    reauthenticateWithCredential,
    reauthenticateWithPopup,
    EmailAuthProvider,
    signOut,
    User
} from "firebase/auth";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Copy of the current ID token for synchronous checks (ProtectedRoute). The Firebase SDK owns the session
// and refreshes the token before it expires (every hour); onIdTokenChanged keeps this copy up to date.
export const TOKEN_STORAGE_KEY = "accessToken";

const storeToken = async (user: User) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, await user.getIdToken());
};

onIdTokenChanged(auth, (user) => {
    if (user) storeToken(user);
});

// Resolves once Firebase has restored a saved session (or found none) after page load
export const authReady: Promise<void> = new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, () => {
        unsubscribe();
        resolve();
    });
});

// Token for API calls. getIdToken() refreshes it when it's about to expire.
// Sessions from before sign-in moved to the frontend only have the stored token, until they sign in again.
export const getAuthToken = async () => {
    await authReady;
    if (auth.currentUser) return auth.currentUser.getIdToken();
    return localStorage.getItem(TOKEN_STORAGE_KEY);
};

export const firebase_sign_in_with_google = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    await storeToken(result.user);
    return result.user;
};

export const firebase_sign_in_with_email = async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await storeToken(result.user);
    return result.user;
};

// Creates the account and sends the verification email
export const firebase_sign_up_with_email = async (name: string, email: string, password: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, {displayName: name});
    await storeToken(result.user);
    // The account works without it, so a failed email shouldn't fail sign-up; it can be resent later
    sendVerificationEmail().catch((err) => console.error("sendEmailVerification failed:", err));
    return result.user;
};

// The link in the email brings the user back to the app after verifying
export const sendVerificationEmail = () => {
    if (!auth.currentUser) return Promise.reject(new Error("NOT_SIGNED_IN"));
    return sendEmailVerification(auth.currentUser, {url: `${window.location.origin}/`});
};

// Re-reads the account from Firebase. Once verified, refreshes the token so the backend sees email_verified.
export const refreshEmailVerified = async () => {
    const user = auth.currentUser;
    if (!user) return false;
    await user.reload();
    if (user.emailVerified) {
        localStorage.setItem(TOKEN_STORAGE_KEY, await user.getIdToken(true));
    }
    return user.emailVerified;
};

// Firebase emails a link to set a new password, then sends the user back to the sign-in page
export const sendPasswordReset = (email: string) => {
    return sendPasswordResetEmail(auth, email, {url: `${window.location.origin}/auth`});
};

// Google-only accounts have no password to reset or re-enter
export const hasPasswordSignIn = () => {
    return Boolean(auth.currentUser?.providerData.some((p) => p.providerId === 'password'));
};

// Confirms it's really the account owner before a sensitive action (deleting the account)
export const reauthenticate = async (password?: string) => {
    const user = auth.currentUser;
    if (!user) throw new Error("NOT_SIGNED_IN");
    if (hasPasswordSignIn()) {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email || '', password || ''));
    } else {
        await reauthenticateWithPopup(user, new GoogleAuthProvider());
    }
};

export const firebase_sign_out = async () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    await signOut(auth);
};
